import * as fs from 'fs';
import { ConsoleLogger } from '../core/types';
import { generateConfig } from './generateConfig.js';

/**
 * 初始化项目工具
 * 快捷方式：@mta 初始化项目
 * 
 * 功能：
 * 1. 从上下文推断项目路径
 * 2. 调用 generateConfig 生成配置
 */
export async function initProject(args: {
    projectPath?: string;
}): Promise<{
    content: Array<{ type: string; text: string }>;
}> {
    const logger = new ConsoleLogger();
    
    try {
        // 1. 确定项目路径
        let projectPath = args.projectPath;
        
        if (!projectPath) {
            // 尝试从当前工作目录推断
            projectPath = process.cwd();
            logger.log(`💡 未指定项目路径，使用当前工作目录: ${projectPath}`);
        }
        
        // 验证路径
        if (!fs.existsSync(projectPath)) {
            return {
                content: [{
                    type: 'text',
                    text: JSON.stringify({
                        error: `项目路径不存在: ${projectPath}`,
                        tip: '请在 Chat 中添加项目文件夹，或明确指定项目路径'
                    }, null, 2)
                }]
            };
        }
        
        logger.log(`🚀 开始初始化项目: ${projectPath}`);
        
        // 2. 调用 generateConfig 生成配置
        const result = await generateConfig({
            projectPath,
            autoMatch: true,
            updateMode: 'merge'
        });
        
        // 3. 解析结果并返回友好提示
        const resultData = JSON.parse(result.content[0].text);
        
        if (resultData.success) {
            const agents = resultData.agents?.map((a: { id?: string; title?: string }) => a.id || a.title) || [];
            
            return {
                content: [{
                    type: 'text',
                    text: JSON.stringify({
                        success: true,
                        message: '🎉 项目初始化成功！',
                        projectPath,
                        configPath: resultData.configPath,
                        agents: agents,
                        summary: `已应用 ${agents.length} 个 Agents: ${agents.join(', ')}`,
                        nextSteps: [
                            '1. 配置文件已生成：.github/copilot-instructions.md',
                            '2. 现在可以开始编码，Copilot 会自动应用项目规范',
                            '3. 使用 @mta 获取具体规范：如 "@mta Vue 3 组件规范"'
                        ]
                    }, null, 2)
                }]
            };
        } else {
            return {
                content: [{
                    type: 'text',
                    text: JSON.stringify({
                        error: resultData.error || '初始化失败',
                        projectPath
                    }, null, 2)
                }]
            };
        }
    } catch (error) {
        logger.error(`初始化失败: ${error}`);
        return {
            content: [{
                type: 'text',
                text: JSON.stringify({
                    error: error instanceof Error ? error.message : String(error)
                }, null, 2)
            }]
        };
    }
}
